import React, { useEffect, useState } from "react";
import { Users, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * FIELD-001B · Read-only project team panel.
 *
 * Shows Name / Trade or Role / Assignment / Work status for every engineer
 * currently attached to the project via a canonical qualifying record
 * (Project Lead, canonical task assignee, non-cancelled survey or
 * non-cancelled appointment). Never exposes private contact info, archived
 * roster or admin actions.
 */
export default function ProjectTeamPanel({ projectId, testid = "project-team-panel" }) {
    const [team, setTeam] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.projectTeam(projectId);
                if (!cancelled) setTeam(r?.team || []);
            } catch (e) {
                if (!cancelled) setError("Team roster unavailable.");
            }
        })();
        return () => { cancelled = true; };
    }, [projectId]);

    return (
        <section className="pe-card p-5 md:p-6 mt-6" data-testid={testid}>
            <div className="flex items-start gap-2 mb-3">
                <Users size={16} strokeWidth={1.75} className="text-[#3E9E2E] mt-0.5" />
                <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg text-[#131B2F] font-medium">Project team</h3>
                    <div className="text-xs text-[#4B5565] mt-0.5">
                        Read-only view of everyone currently working on this project.
                    </div>
                </div>
            </div>
            {error && <div className="text-sm text-[#DA3535]" data-testid={`${testid}-error`}>{error}</div>}
            {!team && !error && (
                <div className="text-sm text-[#4B5565] flex items-center gap-2" data-testid={`${testid}-loading`}>
                    <Loader2 size={14} className="animate-spin" /> Loading team…
                </div>
            )}
            {team && team.length === 0 && (
                <div className="text-sm text-[#4B5565]" data-testid={`${testid}-empty`}>
                    No engineers currently assigned to this project.
                </div>
            )}
            {team && team.length > 0 && (
                <ul className="divide-y divide-[#F0F1F5]" data-testid={`${testid}-list`}>
                    {team.map((m) => (
                        <li key={m.engineer_id}
                            data-testid={`${testid}-row-${m.engineer_id}`}
                            className="py-3 flex items-start gap-3">
                            <span
                                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0"
                                style={{ background: m.colour || "#131B2F" }}
                            >
                                {m.initials || (m.display_name || "?").slice(0, 2).toUpperCase()}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#131B2F] font-medium truncate">
                                    {m.display_name}
                                    {m.is_project_lead && (
                                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#EAF6E1] text-[#2E7E22]"
                                              data-testid={`${testid}-lead-pill-${m.engineer_id}`}>
                                            Project Lead
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-[#4B5565] truncate">
                                    {(m.trade || m.role || "—")}
                                </div>
                                {m.assignments && m.assignments.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {m.assignments.map((a, idx) => (
                                            <span key={idx}
                                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-[#F5F5F7] text-[#131B2F]">
                                                {a}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span className="text-[11px] text-[#4B5565] shrink-0 pt-0.5"
                                  data-testid={`${testid}-status-${m.engineer_id}`}>
                                {m.work_status}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
